import { supabase } from '../lib/supabase';

/**
 * Service for managing coaching concepts - sales principles and techniques
 * Handles snake_case (DB) to camelCase (React) conversion
 */
export const coachingConceptsService = {
  /**
   * Get all coaching concepts (public read access)
   * @param {Object} filters - Optional filters
   * @param {string} filters.industry - Filter by industry
   * @param {number} filters.difficulty - Filter by difficulty level
   * @param {string} filters.searchTerm - Search in title, summary, or tags
   * @returns {Promise<Array>} Array of coaching concepts
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('coaching_concepts')?.select('*')?.order('created_at', { ascending: false });

      // Apply filters if provided
      if (filters?.industry) {
        query = query?.eq('industry', filters?.industry);
      }

      if (filters?.difficulty) {
        query = query?.eq('difficulty', filters?.difficulty);
      }

      if (filters?.searchTerm) {
        query = query?.or(`title.ilike.%${filters?.searchTerm}%,summary.ilike.%${filters?.searchTerm}%,tags.ilike.%${filters?.searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase for React
      return data?.map(concept => ({
        id: concept?.id,
        industry: concept?.industry,
        title: concept?.title,
        summary: concept?.summary,
        bulletPoints: concept?.bullet_points,
        exampleScript: concept?.example_script,
        source: concept?.source,
        tags: concept?.tags,
        difficulty: concept?.difficulty,
        createdAt: concept?.created_at
      }));
    } catch (error) {
      console.error('Error fetching coaching concepts:', error);
      throw error;
    }
  },

  /**
   * Get a single coaching concept by ID
   * @param {string} id - Concept ID
   * @returns {Promise<Object>} Coaching concept
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('coaching_concepts')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        industry: data?.industry,
        title: data?.title,
        summary: data?.summary,
        bulletPoints: data?.bullet_points,
        exampleScript: data?.example_script,
        source: data?.source,
        tags: data?.tags,
        difficulty: data?.difficulty,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error fetching coaching concept:', error);
      throw error;
    }
  },

  /**
   * Create a new coaching concept (admin only)
   * @param {Object} concept - Concept data in camelCase
   * @returns {Promise<Object>} Created concept
   */
  async create(concept) {
    try {
      // Verify user is authenticated
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for DB
      const { data, error } = await supabase?.from('coaching_concepts')?.insert({
          industry: concept?.industry,
          title: concept?.title,
          summary: concept?.summary,
          bullet_points: concept?.bulletPoints,
          example_script: concept?.exampleScript,
          source: concept?.source,
          tags: concept?.tags,
          difficulty: concept?.difficulty
        })?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        industry: data?.industry,
        title: data?.title,
        summary: data?.summary,
        bulletPoints: data?.bullet_points,
        exampleScript: data?.example_script,
        source: data?.source,
        tags: data?.tags,
        difficulty: data?.difficulty,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating coaching concept:', error);
      throw error;
    }
  },

  /**
   * Update a coaching concept (admin only)
   * @param {string} id - Concept ID
   * @param {Object} updates - Updates in camelCase
   * @returns {Promise<Object>} Updated concept
   */
  async update(id, updates) {
    try {
      // Verify user is authenticated
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for DB
      const dbUpdates = {};
      if (updates?.industry !== undefined) dbUpdates.industry = updates?.industry;
      if (updates?.title !== undefined) dbUpdates.title = updates?.title;
      if (updates?.summary !== undefined) dbUpdates.summary = updates?.summary;
      if (updates?.bulletPoints !== undefined) dbUpdates.bullet_points = updates?.bulletPoints;
      if (updates?.exampleScript !== undefined) dbUpdates.example_script = updates?.exampleScript;
      if (updates?.source !== undefined) dbUpdates.source = updates?.source;
      if (updates?.tags !== undefined) dbUpdates.tags = updates?.tags;
      if (updates?.difficulty !== undefined) dbUpdates.difficulty = updates?.difficulty;

      const { data, error } = await supabase?.from('coaching_concepts')?.update(dbUpdates)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        industry: data?.industry,
        title: data?.title,
        summary: data?.summary,
        bulletPoints: data?.bullet_points,
        exampleScript: data?.example_script,
        source: data?.source,
        tags: data?.tags,
        difficulty: data?.difficulty,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error updating coaching concept:', error);
      throw error;
    }
  },

  /**
   * Delete a coaching concept (admin only)
   * @param {string} id - Concept ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      // Verify user is authenticated
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('coaching_concepts')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting coaching concept:', error);
      throw error;
    }
  },

  /**
   * Get unique industries from coaching concepts
   * @returns {Promise<Array<string>>} Array of unique industries
   */
  async getIndustries() {
    try {
      const { data, error } = await supabase?.from('coaching_concepts')?.select('industry');

      if (error) throw error;

      // Extract unique industries
      const industries = [...new Set(data.map(item => item.industry))];
      return industries?.sort();
    } catch (error) {
      console.error('Error fetching industries:', error);
      throw error;
    }
  }
};

export default coachingConceptsService;